import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Divider,
  Fade,
  IconButton,
  List,
  ListItemButton,
  Paper,
  Tab,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import FormatListBulletedOutlinedIcon from "@mui/icons-material/FormatListBulletedOutlined";
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import QrCodeIcon from '@mui/icons-material/QrCode';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { MapkitProvider } from "react-mapkit";
import { AppColor, BeaconViewMode, DesignViewMode, ExperienceViewMode, Fonts } from "../../../types/Constants";
import { BusinessGroup } from "../../../types/models/BusinessGroup";
import styles from "./Beacons.module.scss";
import {
  createBeaconGroup,
  deleteBusinessGroup,
  getMapkitToken,
  getMyBusinessGroups,
  setCurrentBusinessGroup,
  updateBeaconGroup,
  archiveBeaconGroup,
  duplicateBeaconGroup,
  shareBeaconGroup,
  deleteBeacon,
  setCurrentIndoorExperience,
  updateIndoorExperience,
} from "../../../redux/actions";
import { useAppDispatch, useAppSelector } from "../../../utils/hooks";
import { fetchError, showMessage } from "../../../redux/actions/Common";
import { Beacon } from "../../../types/models/Beacon";
import { BeaconColumnInfo, BeaconAllColumnKeys } from "./list/BeaconListTypes";
import BeaconList from "./list/BeaconList";
import BeaconListActions from "./list/BeaconListActions";
import CalenderActions from "./calender/CalenderActions";
import MapView from "./map/MapView";
import MapViewActions from "./map/MapViewActions";
import CardsViewActions from "./card/CardsViewActions";
import TextInputDialog from "../../../components/TextInputDialog";
import BeaconImportDialog from "../components/creatingBeacon/BeaconImportDialog";
import { BeaconDialogModes } from "../components/creatingBeacon/BeaconDialog";
import AppConfirmDialog from "../../../components/AppConfirmDialog";
import { useAuthContext } from "../../authentication/contexts/AuthContext";
import BeaconGroupMenu from "../../../components/BeaconGroupMenu";
import Categories from "./categories/Categories";
import { AnalyticsOutlined, ExpandLess, ExpandMore, PostAddOutlined } from "@mui/icons-material";
import Analytics from "../pages/analytics/Analytics";
import BeaconEditor from "../components/creatingBeacon/BeaconEditor";
import TutorialPage from "./tutorial/TutorialPage";
import ExperienceEditor, { BeaconEditorModes } from "./experience/ExperienceEditor";
import { checkLimit } from "../../../redux/actions/Subscription";
import RealEstateListing from "./realestate/RealEstateListing";
import IndoorBeaconList from "./list/IndoorBeaconList";
import { IndoorExperience } from "../../../types/models/IndoorExperience";
import { isTestingEnvironment } from "../../../utils/utils";
import QRCodeDisplay from "./settings/QRCodeDisplay";
import EditIndoorDialog from "../../../components/EditIndoorDialog";

const BeaconsIndex: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  let { group_id } = useParams();
  let { tab } = useParams();
  let { view } = useParams();
  const dispatch = useAppDispatch();
  const auth = useAuthContext();

  const businessGroupState = useAppSelector((state) => state.businessGroup);
  const beaconGroupState = useAppSelector((state) => state.beacon);

  const [theme, setTheme] = useState("default");
  const [selectedBeacon, setSelectedBeacon] = useState<Beacon>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [viewMode, setViewMode] = useState(BeaconViewMode.Map);
  const [typeFilterTabValue, setTypeFilterTabValue] = useState("1");
  const [openCreateGroup, setOpenCreateGroup] = useState<boolean>(false);
  const [openCreateShareGroup, setOpenCreateShareGroup] = useState<boolean>(false);
  const [openEditGroup, setOpenEditGroup] = useState<boolean>(false);
  const [openEditSubdomain, setOpenEditSubdomain] = useState<boolean>(false);
  const [openEditIndoor, setOpenEditIndoor] = useState<boolean>(false);

  const [showExperiences, setShowExperiences] = useState(true);
  const [showShared, setShowShared] = useState(true);
  const [showArchived, setShowArchived] = useState(true);
  const [showIndoor, setShowIndoor] = useState(true);

  const [openBeaconDialog, setOpenBeaconDialog] = useState<boolean>(false);
  const [openBeaconImportDialog, setOpenBeaconImportDialog] = useState<boolean>(false);
  const [beaconDialogEditMode, setBeaconDialogEditMode] = useState<(typeof BeaconDialogModes)[number]>("CREATE");
  const [openEditSpecialBeaconMessage, setOpenEditSpecialBeaconMessage] = useState<boolean>(false);
  const [openExperienceError, setOpenExperienceError] = useState<boolean>(false);
  const [openBeaconError, setOpenBeaconError] = useState<boolean>(false);
  const [openBeaconsInExpError, setOpenBeaconsInExpError] = useState<boolean>(false);
  const [openDeleteBeacon, setOpenDeleteBeacon] = useState<boolean>(false);

  const [beaconListInfo, setBeaconListInfo] = useState<BeaconColumnInfo[]>([]);
  const [lastBeaconGroup, setLastBeaconGroup] = useState<string>("");
  const [token, setToken] = useState<{
    tokenOrCallback: string;
  }>({
    tokenOrCallback: "",
  });
  const [refreshDataFlag, setRefreshDataFlag] = useState<boolean>(false);

  const [checkLimits, setCheckLimit] = useState<boolean>(false);
  const [experienceLimit, setExperienceLimit] = useState<number>();
  const [beaconsInExpLimit, setBeaconsInExpLimit] = useState<number>();

  const [tabValue, setTabValue] = useState("beacons");
  const [pageMode, setPageMode] = useState("dashboard");
  const [themeColor, setThemeColor] = useState("#ffffff");
  const [title, setTitle] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [logo, setLogo] = useState<File | null>(null);
  const [subdomain, setSubdomain] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [openIndoorEditor, setOpenIndoorEditor] = useState(false);

  const handleTabValueChange = (event: React.SyntheticEvent, newValue: string) => {
    setTabValue(newValue);
    switch (newValue) {
      case "beacons":
        setViewMode(BeaconViewMode.Map);
        return;
      case "design":
        setViewMode(DesignViewMode.Category);
        return;
      case "editor":
        setViewMode(BeaconViewMode.Editor);
        return;
      case "experience":
        setViewMode(ExperienceViewMode.Experience);
        return;
      default:
        setViewMode(BeaconViewMode.Map);
    }
  };

  useEffect(() => {
    setIsLoading(true);

    let lastBeaconGroup = localStorage.getItem("lastBeaconGroup") ?? "";
    setLastBeaconGroup(lastBeaconGroup);

    const fetchBusinessGroups = () =>
      new Promise<void>((resolve) => {
      dispatch(
        getMyBusinessGroups(({ businessGroups }) => {
          if (businessGroups) {
            let currentGroup: BusinessGroup | null = null;
            if (group_id) {
              currentGroup = businessGroups.find((group) => group.id === group_id) ?? null;
              localStorage.setItem("lastBeaconGroup", currentGroup?.id ?? "");
              dispatch(setCurrentBusinessGroup(currentGroup));
            } else if (lastBeaconGroup) {
              currentGroup = businessGroups.find((group) => group.id === lastBeaconGroup) ?? null;
              dispatch(setCurrentBusinessGroup(currentGroup));
              navigate("/beacons/map/" + currentGroup?.id);
            }
          }
        resolve();
        })
      );
    });

    // Get mapkit token
    const fetchMapkitToken = () =>
      new Promise<void>((resolve) => {
      dispatch(
        getMapkitToken((success, token) => {
          if (success && token) {
            setToken({ tokenOrCallback: token });
          } else {
            console.error("Failed to fetch Mapkit token");
          }
          resolve();
        })
      );
    });

    Promise.all([fetchBusinessGroups(), fetchMapkitToken()])
    .then(() => {
      setIsLoading(false);
    });

    //load columnInfo from session
    var tempBeaconInfo: BeaconColumnInfo[] = [];
    BeaconAllColumnKeys.forEach((value: string) => {
      let sessionColumnVisibility = sessionStorage.getItem(value + "_beacon_column_visibility");

      //default visibility is true
      var columnVisibility: boolean = true;
      if (sessionColumnVisibility == null) {
        //store default value if not initialised
        sessionStorage.setItem(value + "_beacon_column_visibility", JSON.stringify(columnVisibility));
      } else {
        columnVisibility = JSON.parse(sessionColumnVisibility);
      }

      tempBeaconInfo.push({
        columnType: value,
        visibility: columnVisibility,
      });
    });
    setBeaconListInfo(tempBeaconInfo);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    beaconListInfo.forEach((beaconInfo) => {
      sessionStorage.setItem(beaconInfo.columnType + "_beacon_column_visibility", JSON.stringify(beaconInfo.visibility));
    });
  }, [beaconListInfo]);

  useEffect(() => {
    let selectedGroup = null;
    if (group_id) {
      selectedGroup = businessGroupState.businessGroups?.find((group) => group.id === group_id) ?? null;
      localStorage.setItem("lastBeaconGroup", selectedGroup?.id ?? "");
    }
    if ((!businessGroupState.currentBusinessGroup && !group_id) || businessGroupState.currentBusinessGroup?.id === group_id) {
      // nothing changed
      return;
    } else {
      dispatch(setCurrentBusinessGroup(selectedGroup));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group_id]);

  useEffect(() => {
    if (view) {
      setViewMode(view);
      setIsEditing(false);
    }
  }, [view]);

  useEffect(() => {
    if (tab) {
      setTabValue(tab);
    }
  }, [tab]);

  useEffect(() => {
    if (location.pathname === "/beacons") {
      setPageMode("dashboard");
    }
  }, [location.pathname]);

  useEffect(() => {
    if (businessGroupState.currentBusinessGroup) {
      setTheme(businessGroupState.currentBusinessGroup.theme ?? theme);
      setTitle(businessGroupState.currentBusinessGroup.title ?? "");
      setDescription(businessGroupState.currentBusinessGroup.description ?? "");
      setThemeColor(businessGroupState.currentBusinessGroup.color ?? "");
      setName(businessGroupState.currentBusinessGroup.name ?? "");
    }
  }, [businessGroupState.currentBusinessGroup]);

  const handleOpenBeaconDialog = (group_id: string, beacon?: Beacon, edit?: boolean) => {
    const group = businessGroupState.currentBusinessGroup;
    if (!hasTabs() || !hasAccessLevel() || group?.archived) {
      //READ_ONLY MODE
      setSelectedBeacon(beacon);
      setBeaconDialogEditMode("READ_ONLY");
      setOpenBeaconDialog(true);
      setIsEditing(true);
      return;
    }
    setSelectedBeacon(beacon);
    setBeaconDialogEditMode(edit ? "EDIT" : "CREATE");
    setOpenBeaconDialog(true);
    navigate(`/beacons/${group_id}/editor/${beacon?.id ?? ""}`);
    setIsEditing(true);
  };

  const handleOpenBeaconDeleteDialog = (beacon?: Beacon) => {
    if (businessGroupState.currentBusinessGroup != null) {
      setOpenDeleteBeacon(true);
      setSelectedBeacon(beacon);
    }
  };

  const handleOpenBeaconImportDialog = () => {
    setOpenBeaconImportDialog(true);
  };

  const handleOpenBeaconLink = (beacon?: Beacon) => {
    if (beacon?.id) {
      const url = "https://beacons.skidattl.com/beacon/" + beacon.id;
      navigator.clipboard.writeText(url);
      dispatch(showMessage("Link copied to clipboard"));
    }
    else {
      dispatch(fetchError("Error copying link"));
    }
  };


  const handleViewModeChange = (viewType: string) => {
    setViewMode(viewType);
  };

  const handleOnTypeChange = (newValue: string) => {
    setTypeFilterTabValue(newValue);
  };

  const handleDuplicateExp = (callback: (result: boolean) => void) => {
    dispatch(checkLimit(businessGroupState.currentBusinessGroup?.id ?? "", (config) => {
      if (config) {
        if (config.totalExperiences >= config.ExperiencesLimit) {
          setOpenExperienceError(true);
          setExperienceLimit(config.ExperiencesLimit);
          callback(false);
        } else {
          callback(true);
        }
      } 
    }));
  };

  const handleNewExperience = () => {
    dispatch(checkLimit(businessGroupState.currentBusinessGroup?.id ?? "", (config) => {
      if (config) {
        if (config.totalExperiences >= config.ExperiencesLimit) {
          setOpenExperienceError(true);
          setExperienceLimit(config.ExperiencesLimit);
        } else {
          setPageMode("create");
          navigate('/beacons/create');
        }
      }
    }));
  };

  const handleNewRealEstateListing = () => {
    setPageMode("realestate");
    navigate('/beacons/realestate');
  };

  const handleNewShareGroup = () => {
    setOpenCreateShareGroup(true);
  };

  const handleCloseNewShareGroup = (code: string | null) => {
    setOpenCreateShareGroup(false);
    if (code != null) {
      navigate("/acceptshare?code=" + code);
    }
  };

  const handleSubmitted = () => {
    // Reload data
    setOpenBeaconDialog(false);
    setIsEditing(false);
    navigate(`/${tabValue}/${viewMode}/${group_id}`);
    setRefreshDataFlag(true);
  };

  const handleExperienceSubmitted = (newGroupId?: string) => {
    // Reload data
    navigate(`/beacons/map/${newGroupId || group_id}`);
    setViewMode(BeaconViewMode.Map);
    setPageMode('dashboard');
    setTabValue('beacons');
    setRefreshDataFlag(true);
  };

  const cancelExperience = () => {
    const targetGroupId = group_id || lastBeaconGroup;
    navigate(`/beacons/map/${targetGroupId}`);
    setViewMode(BeaconViewMode.Map);
    setPageMode('dashboard');
    setTabValue('beacons');
  };
  

  const getBeaconViewAction = () => {
    switch (viewMode) {
      case BeaconViewMode.List:
        return <BeaconListActions beaconColumnInfo={beaconListInfo} setBeaconColumnInfo={setBeaconListInfo} />;
      case BeaconViewMode.Calendar:
        return <CalenderActions />;
      case BeaconViewMode.Map:
        return <MapViewActions />;
      case BeaconViewMode.Card:
        return <CardsViewActions />;
      default:
        return <Box sx={{ display: "flex", alignItems: "end", minHeight: 48, marginBottom: 1 }} />;
    }
  };

  const getBeaconView = () => {
    if (isLoading || !token.tokenOrCallback) {
      return (
        <Box sx=
          {{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh"
          }}>
          <CircularProgress />
        </Box>
      );
    } else {
      switch (viewMode) {
        case BeaconViewMode.List:
          return (
            // Put <Fade> element for each view to make animation work
            <Fade in={true} timeout={2000}>
              <Paper sx={{ m: 1, backgroundColor: "white" }} elevation={1}>
                <BeaconList
                  context={"BEACONS"}
                  beaconColumnInfo={beaconListInfo}
                  refreshFlag={refreshDataFlag}
                  setRefreshFlag={setRefreshDataFlag}
                  onOpenBeaconDialog={handleOpenBeaconDialog}
                  onOpenBeaconDeleteDialog={handleOpenBeaconDeleteDialog}
                  onOpenBeaconLink={handleOpenBeaconLink}
                  isArchivedView={false}
                  isArchivedGroup={businessGroupState.currentBusinessGroup?.archived}
                  key={"list"}
                />
              </Paper>
            </Fade>
          );
        case BeaconViewMode.Calendar:
          return (
            <Fade in={true}>
              <Paper sx={{ m: 0, backgroundColor: "white" }} elevation={1}>
                {/* <CalenderView onOpenBeaconDialog={handleOpenBeaconDialog} refreshFlag={refreshDataFlag} setRefreshFlag={setRefreshDataFlag} /> */}
              </Paper>
            </Fade>
          );
        case BeaconViewMode.Map:
          return (
            <Paper sx={{ m: 0, backgroundColor: "white" }} elevation={1}>
              <MapkitProvider {...token}
              >
                <MapView onOpenBeaconDialog={handleOpenBeaconDialog} refreshFlag={refreshDataFlag} setRefreshFlag={setRefreshDataFlag} context="BEACONS" />
              </MapkitProvider>
            </Paper>
          );
        case BeaconViewMode.Card:
          return (
            <Fade in={true}>
              <Box>
                {/* <CardsView onOpenBeaconDialog={handleOpenBeaconDialog} onOpenBeaconArchiveDialog={handleOpenBeaconDeleteDialog} onOpenBeaconLink={handleOpenBeaconLink} refreshFlag={refreshDataFlag} setRefreshFlag={setRefreshDataFlag} /> */}
              </Box>
            </Fade>
          );
        case BeaconViewMode.Analytics:
          return (
            <Analytics />
          );
        default:
          return <Box />;
      }
    }
  };

  const getDesignView = () => {
    switch (viewMode) {
      case DesignViewMode.Category:
        return (
          <Categories
            indexLoading={isLoading}
          />
        );
      case DesignViewMode.LandingPage:
        return (
          // <SettingsPage
          //   cancelSettingsForm={cancelSettingsForm}
          //   handleDeleteGroup={handleDeleteGroup}
          // />
          <></>
        );
      default:
        return (<></>);
    }
  };

  const getExperienceViewMode = () => {
    var group = businessGroupState.currentBusinessGroup; 
    var mode: typeof BeaconEditorModes[number] = "EDIT";
    if (group?.archived || group?.access_level == "read") {
      mode = "READ_ONLY";
    }
    switch (viewMode) {
        default: 
        return (
          <ExperienceEditor 
          onSubmitted={handleExperienceSubmitted}
          mode={mode}
          refreshFlag={refreshDataFlag}
          setRefreshFlag={setRefreshDataFlag}
          isOpen={openBeaconDialog}
          onClose={() => {setOpenBeaconDialog(false); setIsEditing(false); navigate(`/${tabValue}/${viewMode}/${group_id}`);}}
          cancelSettingsForm={cancelExperience}
          handleDeleteGroup={() => handleDeleteGroup}
          heightOffset={285}
        />
        );
    }
  };

  const getPageView = () => {
    switch (pageMode) {
      case "dashboard":
        return (
          getDashboardPage()
        );
      case "create":
          return (
            <ExperienceEditor 
              onSubmitted={handleExperienceSubmitted}
              mode={"CREATE"}
              refreshFlag={refreshDataFlag}
              setRefreshFlag={setRefreshDataFlag}
              isOpen={openBeaconDialog}
              onClose={() => {setOpenBeaconDialog(false); setIsEditing(false); navigate(`/${tabValue}/${viewMode}/${group_id}`);}}
              cancelSettingsForm={cancelExperience}
              handleDeleteGroup={() => handleDeleteGroup}
              heightOffset={140}
            />
          );
      case "realestate":
          return (
            <MapkitProvider {...token}>
            <RealEstateListing 
              mode={"EDIT"}
              cancelSettingsForm={cancelExperience}
              onComplete={handleExperienceSubmitted}
            />
            </MapkitProvider>
          );
      case "indoor":
          return (
            <Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", pb: 2 }}>
                <Box>
                  {getBeaconPageTitle(businessGroupState.currentIndoorExperience)}
                  { !isLoading && businessGroupState.currentIndoorExperience?.subdomain && (
                  <Typography sx={{ mt: -4, mb: 2 }} gutterBottom>
                    ar.skidattl.com/i/{businessGroupState.currentIndoorExperience?.subdomain}
                  </Typography>
                  )}
                  <Button
                    variant="contained"
                    sx={{ borderRadius: 6 }}
                    onClick={() => setOpenEditIndoor(true)}
                  >
                    Edit
                  </Button>
                </Box>
                { businessGroupState.currentIndoorExperience?.subdomain && (
                  <Box sx={{ mt: 4 }}>
                    <QRCodeDisplay
                      identifier={businessGroupState.currentIndoorExperience?.subdomain ?? ""}
                      width={80}
                      height={80}
                    />
                  </Box>
                )}
              </Box>
              <IndoorBeaconList 
                refreshFlag={refreshDataFlag}
                setRefreshFlag={setRefreshDataFlag}
                openIndoorBeaconEditor={openIndoorEditor}
                setOpenIndoorBeaconEditor={setOpenIndoorEditor}
              />
            </Box>
          );
      default:
        return (<></>);
    }
  };

  const hasTabs = () => {
    //Check if personal or special group is selected
    if (businessGroupState.currentBusinessGroup == null || businessGroupState.currentBusinessGroup.special_event) {
      return false;
    } else {
      return true;
    }
  };

  const hasAccessLevel = (group?: BusinessGroup | null) => {
    var selectedGroup = group;
    if (group == null) {
      selectedGroup = businessGroupState.currentBusinessGroup;
    }
    if (selectedGroup?.is_shared) {
      if (selectedGroup?.access_level == "readwrite" || selectedGroup?.access_level == "admin") {
        return true;
      }
      return false;
    }
    return true;
  };

  const getBeaconPageTitle = (group: BusinessGroup | IndoorExperience| null | undefined) => {
    //if the user selected the 'null' group, then it is the personal group
    var groupName = group?.name ?? "";

    if (isLoading) {
      return (
        <Box>
          <Skeleton highlightColor={AppColor.skeletonHighlight} baseColor={AppColor.skeletonBase} height={28} width={200} count={1} />
          <Box sx={{ mt: 2 }}>
            <Skeleton highlightColor={AppColor.skeletonHighlight} baseColor={AppColor.skeletonBase} width="80%" height={15} count={1} />
          </Box>
        </Box>
      );
    } else {
      return (
        <>
          <Box
            sx={{
              marginY: 2,
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Box
              sx={{
                display: "inline",
                alignItems: "center",
                justifyContent: "flex-start",
                "&:hover": {
                  "& .MuiIconButton-root": {
                    display: "block",
                  },
                },
              }}
            >
              <Typography sx={{ mt: 1, fontSize: 20, fontWeight: Fonts.SEMI_BOLD }} gutterBottom>
                {groupName}
              </Typography>
            </Box>
          </Box>
        </>
      );
    }
  };

  const getIndexButtons = (group_id: string) => {
    return (
      <Box display="flex" justifyContent="flex-start" sx={{ padding: 0, gap: 2 }}>
        {hasTabs() && hasAccessLevel() && !businessGroupState.currentBusinessGroup?.archived && (
          <>
            <Button variant="contained" sx={{ borderRadius: 6 }} disabled={checkLimits} onClick={() => {
                setCheckLimit(true);
                dispatch(checkLimit(businessGroupState.currentBusinessGroup?.id ?? "", (config) => {
                  if (config) {
                    if (config.BeaconsPerExpLimit && config.totalBeaconsExp >= config.BeaconsPerExpLimit) {
                      setOpenBeaconsInExpError(true);
                      setBeaconsInExpLimit(config.BeaconsPerExpLimit);
                    } else {
                      handleOpenBeaconDialog(group_id);
                    }
                  } 
                  setCheckLimit(false);
                }));
            }}>
              New Beacon
            </Button>
            <Button
              sx={{
                backgroundColor: AppColor.orangeBackground,
                ml: 1,
                borderRadius: 6,
              }}
              variant="outlined" onClick={() => handleOpenBeaconImportDialog()}
            >
              Import
            </Button>
          </>
        )}
      </Box>
    );
  };

  const getDashboardPage = () => {
    return ( 
        <Box sx={{ paddingX: 2 }}>
          {!isLoading ? (
          <>
            <TutorialPage />
          </>
          ) : (
          <Box>
            <Skeleton highlightColor={AppColor.skeletonHighlight} baseColor={AppColor.skeletonBase} height={28} width={200} count={1} />
            <Box sx={{ mt: 2 }}>
              <Skeleton highlightColor={AppColor.skeletonHighlight} baseColor={AppColor.skeletonBase} width="80%" height={15} count={1} />
            </Box>
          </Box>
          )}
        </Box>
    );
  };

  function onGroupSelected(selectedGroup: BusinessGroup | null) {
    dispatch(setCurrentIndoorExperience(null));
    // Change URL
    if (selectedGroup != null) {
      dispatch(setCurrentBusinessGroup(selectedGroup));
      if (tabValue === "experience" || viewMode == "indoor") {
        setTabValue("beacons");
        setViewMode(BeaconViewMode.Map);
        navigate(`/beacons/map/${selectedGroup.id}`);
      } else if (isEditing) {
        setIsEditing(false);
        setPageMode("dashboard");
        navigate(`/${tabValue}/${viewMode}/${selectedGroup.id}`);
      } else {
        setPageMode("dashboard");
        navigate({ pathname: "/" + tabValue + "/" + viewMode + "/" + selectedGroup.id });
      }
    } else {
      //Go back to archived group
      setTabValue("beacons");
      setIsEditing(false);
      setViewMode(BeaconViewMode.Map);
      navigate({ pathname: "/beacons" });
    }
  }

  function onIndoorSelected(selectedGroup: IndoorExperience | null) {
    dispatch(setCurrentBusinessGroup(null));
    // Change URL
    if (selectedGroup != null) {
      dispatch(setCurrentIndoorExperience(selectedGroup));
      setPageMode("indoor");
      setOpenIndoorEditor(false);
      navigate(`/beacons/indoor/${selectedGroup.id}`);
    }
  }

  function onTabSelected(tabMode: string, selectedGroup: BusinessGroup) {
    // Change URL
    if (selectedGroup != null) {
      switch (tabMode) {
        case "beacons":
          navigate({ pathname: "/" + tabMode + "/map/" + selectedGroup.id });
          return;
        case "design":
          navigate({ pathname: "/" + tabMode + "/category/" + selectedGroup.id });
          return;
        case "experience":
          navigate({ pathname: "/" + tabMode + "/existing/" + selectedGroup.id });
          return;
      }
    }
  }

  function onViewSelected(viewMode: string, selectedGroup: BusinessGroup) {
    // Change URL
    if (selectedGroup != null) {
      navigate({ pathname: "/" + tabValue + "/" + viewMode + "/" + selectedGroup.id });
    } else {
      setTabValue("beacons");
      setViewMode(BeaconViewMode.Map);
      navigate({ pathname: "/beacons" });
    }
  }

  function handleCreateGroup(name: string | null, identifier: string | null) {
    if (name != null && identifier != null) {
      setIsLoading(true);
      dispatch(
        createBeaconGroup(name, identifier, (result: boolean) => {
          if (result) {
            dispatch(
              getMyBusinessGroups(({ businessGroups }) => {
                if (businessGroups) {
                  // Change the selected group to the added/edited group
                  const currentGroup = businessGroups.find((group) => group.name === name) ?? null;
                  if (currentGroup) {
                    navigate({
                      pathname: "/beacons/map/" + currentGroup.id,
                    });
                    setTabValue("beacons");
                    setPageMode("dashboard");
                  }
                }
                setIsLoading(false);
              })
            );
          } else {
            setIsLoading(false);
          }
        })
      );
    } else {
      setPageMode("dashboard");
    }
  }

  function closeEditGroupDialog(name: string | null) {
    setOpenEditGroup(false);
    if (name != null && businessGroupState.currentBusinessGroup) {
      setIsLoading(true);
      dispatch(
        updateBeaconGroup(
          businessGroupState.currentBusinessGroup.id,
          name,
          businessGroupState.currentBusinessGroup.subdomain,
          businessGroupState.currentBusinessGroup.theme ?? theme,
          (result: boolean) => {
            if (result) {
              dispatch(
                getMyBusinessGroups(({ businessGroups }) => {
                  if (businessGroups && group_id) {
                    // Change the selected group when the URL includes business group id
                    const currentGroup = businessGroups.find((group) => group.id === group_id) ?? null;
                    dispatch(setCurrentBusinessGroup(currentGroup));
                  }
                  setIsLoading(false);
                })
              );
            } else {
              setIsLoading(false);
            }
          }
        )
      );
    }
  }

  function closeEditIndoor(name: string | null, identifier: string | null) {
    setOpenEditIndoor(false);
    if (name != null && businessGroupState.currentIndoorExperience) {
      setIsLoading(true);
      dispatch(
        updateIndoorExperience(
          businessGroupState.currentIndoorExperience.id,
          name,
          identifier,
          (result: boolean) => {
            if (result) {
              dispatch(
                getMyBusinessGroups(({ indoorExperiences }) => {
                  if (indoorExperiences && group_id) {
                    // Change the selected group when the URL includes business group id
                    const currentGroup = indoorExperiences.find((group) => group.id === group_id) ?? null;
                    dispatch(setCurrentIndoorExperience(currentGroup));
                  }
                  setIsLoading(false);
                })
              );
            } else {
              setIsLoading(false);
            }
          }
        )
      );
    }
  }

  function GetGroupSections(): JSX.Element {
    const specialGroups = businessGroupState.businessGroups?.filter((x) => x.special_event) || [];
    const myGroups = businessGroupState.businessGroups?.filter((x) => !x.special_event && !x.archived && !x.is_shared) || [];
    const mySharedGroups = businessGroupState.businessGroups?.filter((x) => x.is_shared) || [];
    const myArchivedGroups = businessGroupState.businessGroups?.filter((x) => x.archived && !x.is_shared) || [];
    const indoorExperiences = businessGroupState?.indoorExperiences ?? [];

    const anchorRef = useRef<(HTMLButtonElement | null)[]>([]);
    const [isOpenBeaconGroupMenu, setIsOpenBeaconGroupMenu] = useState<number | null>(null);

    useEffect(() => {
      if (businessGroupState.businessGroups) {
        anchorRef.current = Array(businessGroupState.businessGroups.length || 0).fill(null);
      }

    }, [businessGroupState.businessGroups]);

    function GroupListItem(group: BusinessGroup | IndoorExperience, index: number, indoor: boolean = false): JSX.Element {
      const name = group.name;
      const id = group.id;

      const handleMenuToggle = (index: number) => {
        setIsOpenBeaconGroupMenu((prevIndex) => (prevIndex === index ? null : index));
      };

      const isSelected = indoor
        ? businessGroupState.currentIndoorExperience?.id === group?.id
        : businessGroupState.currentBusinessGroup?.id === group?.id;
      
      return (
        <ListItemButton
          selected={isSelected}
          className={`${styles.beacons_groupsItem}`}
          key={id}
          onClick={() => indoor ? onIndoorSelected(group as IndoorExperience) : onGroupSelected(group as BusinessGroup)}
          sx={{ 
            paddingTop: "0px", 
            paddingBottom: "0px",
            paddingLeft: "12px", 
            minHeight: "40px", 
            borderLeft: isSelected ? "4px solid rgba(255, 103, 31, 1)" : "4px solid transparent",
            marginLeft: 0,
          }}
        >
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box>{name}</Box>
            {/* Hide button for Personal group/ specialevent and readonly shared groups */}
            {((!indoor && hasAccessLevel(group as BusinessGroup) && (group as BusinessGroup).special_event == false)) && (
              <>
                <IconButton
                  size="small"
                  ref={(el) => (anchorRef.current[index] = el)}
                  onClick={() => { handleMenuToggle(index); }}
                >
                  <MoreVertIcon sx={{ padding: 0 }} />
                </IconButton>
                <BeaconGroupMenu
                  open={isOpenBeaconGroupMenu === index}
                  anchorElement={anchorRef.current[index]}
                  setIsOpen={(isOpen) => setIsOpenBeaconGroupMenu(isOpen ? index : null)}
                  editFunction={() => setOpenEditGroup(true)}
                  archiveFunction={handleArchiveGroup}
                  deleteFunction={handleDeleteGroup}
                  duplicateFunction={handleDuplicateGroup}
                  shareFunction={handleShareGroup}
                />
              </>
            )}
          </Box>
        </ListItemButton>
      );
    }

    return (
      <Box>
          <Box sx={{ display: "flex", justifyContent: "center", flexDirection: "column", margin: 4 }}>
            <Typography sx={{ fontWeight: Fonts.BOLD, fontSize: 20 }}>New Experience</Typography>
            {isTestingEnvironment() ? (
              <>
              <Typography sx={{ fontSize: 14 }}>Would you like to start from a template?</Typography>
              <Box sx={{ display: "flex", marginTop: 2, justifyContent: "center", width: "100%", gap: 2 }}>
                <Button 
                  sx={{
                    whiteSpace: "nowrap"
                  }}
                  variant="outlined"
                  onClick={() => handleNewRealEstateListing()}
                  startIcon={<PostAddOutlined/>}
                >
                  Real Estate
                </Button>
                <Button
                  variant="contained"
                  onClick={() => handleNewExperience()}
                  startIcon={<AddCircleOutlineIcon/>}
                >
                  Blank
                </Button>
              </Box>
              </>
            ) : (
              <Box sx={{ display: "flex", marginTop: 2, justifyContent: "center", width: "100%" }}>
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => handleNewExperience()}
                  startIcon={<AddCircleOutlineIcon/>}
                  sx={{
                    fontSize: 12,
                    paddingY: 0.25,
                    paddingX: 6,
                    minHeight: "30px",
                    lineHeight: 1.2
                  }}
                >
                  Create New Experience
                </Button>
              </Box>
            )}
          </Box>
        {specialGroups.length > 0 && (
          <Box sx={{ paddingTop: 5, paddingBottom: 5 }}>
            <Typography className={styles.beacons_groupsTitle}>API Integration</Typography>
            <List>
              {specialGroups.map((group, index) => GroupListItem(group, 0))}
            </List>
          </Box>
        )}

        {specialGroups.length > 0 && <Divider />}
        <Box sx={{ paddingBottom: "10px" }}>
          <Box sx={{ backgroundColor: "#FAFAFA", paddingY: 2, display: "flex", justifyContent: "space-between", alignItems: "center", minHeight: "40px" }}>
          <Typography className={styles.beacons_groupsTitle}>Experiences</Typography>
          <IconButton size="small" sx={{ mr: 4 }} onClick={() => setShowExperiences(prev => !prev)}>
            {showExperiences ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
          </Box>
          <Collapse in={showExperiences} timeout="auto" unmountOnExit>
            <List sx={{ paddingY: 0 }}>
              {myGroups.map((group, index) => GroupListItem(group, index))}
            </List>
          </Collapse>
          <Box sx={{ backgroundColor: "#FAFAFA", paddingY: 2, display: "flex", justifyContent: "space-between", alignItems: "center", minHeight: "40px" }}>
          <Typography className={styles.beacons_groupsTitle}>Shared With Me</Typography>
          <IconButton size="small" sx={{ mr: 4 }} onClick={() => setShowShared(prev => !prev)}>
            {showShared ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
          </Box>
          <Collapse in={showShared} timeout="auto" unmountOnExit>
            <List sx={{ paddingY: 0 }}>
              {mySharedGroups.map((group, index) => GroupListItem(group, index + myGroups.length))}
            </List>
          </Collapse>
          {myArchivedGroups.length > 0 && (
            <>
              <Box sx={{ backgroundColor: "#FAFAFA", paddingY: 2, display: "flex", justifyContent: "space-between", alignItems: "center", minHeight: "40px" }}>
              <Typography className={styles.beacons_groupsTitle}>Archived Experiences</Typography>
              <IconButton size="small" sx={{ mr: 4 }} onClick={() => setShowArchived(prev => !prev)}>
                {showArchived ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
              </Box>
              <Collapse in={showArchived} timeout="auto" unmountOnExit>
                <List sx={{ paddingY: 0 }}>
                  {myArchivedGroups.map((group, index) => GroupListItem(group, index + myGroups.length + mySharedGroups.length))}
                </List>
              </Collapse>
            </>
          )}
          {indoorExperiences.length > 0 && (
            <>
              <Box sx={{ backgroundColor: "#FAFAFA", paddingY: 2, display: "flex", justifyContent: "space-between", alignItems: "center", minHeight: "40px" }}>
              <Typography className={styles.beacons_groupsTitle}>Indoor Experiences</Typography>
              <IconButton size="small" sx={{ mr: 4 }} onClick={() => setShowIndoor(prev => !prev)}>
                {showIndoor ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
              </Box>
              <Collapse in={showIndoor} timeout="auto" unmountOnExit>
                <List sx={{ paddingY: 0 }}>
                  {indoorExperiences.map((group, index) => GroupListItem(group, index + myGroups.length + mySharedGroups.length + myArchivedGroups.length, true))}
                </List>
              </Collapse>
            </>
          )}
        </Box>
      </Box>
    );
  }

  const handleDeleteGroup = (groupId: string) => {
    if (!groupId) return;

    setIsLoading(true);
    dispatch(
      deleteBusinessGroup(groupId, (result: boolean) => {
        if (result) {
          dispatch(
            getMyBusinessGroups(() => {
              if (result) {
                dispatch(setCurrentBusinessGroup(null));
              }
              // Move to Personal group to see the moved beacons
              navigate({ pathname: "/beacons" });
              setPageMode("dashboard");
              setTabValue("beacons");
              setIsLoading(false);
            })
          );
        }
      })
    );
  };

  const handleArchiveGroup = (groupId: string) => {
    if (!groupId) return;
    var archived = businessGroupState.currentBusinessGroup?.archived;
    setIsLoading(true);
    dispatch(
      archiveBeaconGroup(groupId, archived ?? false, (result: boolean) => {
        if (result) {
          dispatch(
            getMyBusinessGroups(() => {
              if (result) {
                dispatch(setCurrentBusinessGroup(null));
              }
              // Move to Personal group to see the moved beacons
              navigate({ pathname: "/beacons" });
              setPageMode("dashboard");
              setTabValue("beacons");
              setIsLoading(false);
            })
          );
        }
      })
    );
    setIsLoading(false);
  };

  const handleDuplicateGroup = async(groupId: string | undefined, name: string | null) => {
    if (!groupId || name == null) return;

    setIsLoading(true);

    try {
        const limitCheck = await new Promise<boolean>((resolve, reject) => {
          handleDuplicateExp(result => {
            if (result) {
              resolve(true);
            } else {
              reject(new Error('Checking subscription limits failed'));
            }
          });
        });
        if (limitCheck) {
          dispatch(
            duplicateBeaconGroup(groupId, name,
              (result: boolean) => {
                if (result) {
                dispatch(
                  getMyBusinessGroups(({ businessGroups }) => {
                    if (businessGroups && group_id) {
                      // Change the selected group when the URL includes business group id
                      const currentGroup = businessGroups.find((group) => group.id === group_id) ?? null;
                      dispatch(setCurrentBusinessGroup(currentGroup));
                    }
                    setIsLoading(false);
                  })
                );
              } else {
                setIsLoading(false);
              }
          }));
        }
    } catch (error) {
      console.error(error);
      setIsLoading(false);
    }
  };

  const handleShareGroup = (groupId: string | undefined, email: string | null, accessLevel: string, callBack: (code?: string) => void) => {
    if (!groupId || email == null) return;
    setIsLoading(true);
    dispatch(
      shareBeaconGroup(groupId, email, accessLevel, (result: boolean, code?: string) => {
        if (result && code) {
          callBack(code);
        }
      })
    );
    setIsLoading(false);

  };

  const handleDeleteBeacon = async () => {
    if (!selectedBeacon) return;

    setOpenDeleteBeacon(false);
    setIsLoading(true);

    try {
      await new Promise<void>((resolve, reject) => {
        dispatch(deleteBeacon(selectedBeacon.display_name, selectedBeacon.id, (result) => {
          if (result) {
            resolve();
          } else {
            reject(new Error('Deleting failed'));
          }
        }));
      });
      setRefreshDataFlag(true);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className={styles.beacons_pageContainer}>
        <TabContext value={tabValue}>
          <div className={styles.beacons_sidebar} style={{ overflowY: "auto", height: "calc(100vh - 57px)" }}>
            <Box display="flex" flexDirection="column">
              {GetGroupSections()}
            </Box>
          </div>
          {businessGroupState.currentBusinessGroup ? (
            <div className={styles.beacons_mainContents}>
              <Box sx={{ width: "100%", typography: "body1", margin: 2 }}>
                {getBeaconPageTitle(businessGroupState.currentBusinessGroup)}
                {isEditing ? (
                  <BeaconEditor 
                  onSubmitted={() => handleSubmitted()}
                  mode={beaconDialogEditMode}
                  isOpen={openBeaconDialog}
                  beacon={selectedBeacon}
                  onOpenBeaconDeleteDialog={handleOpenBeaconDeleteDialog}
                  onClose={() => {setOpenBeaconDialog(false); setIsEditing(false); navigate(`/${tabValue}/${viewMode}/${group_id}`);}}
                  mapToken={token}
                  />
                ) : (
                  <>
                  {getIndexButtons(group_id ?? "")}
                  <Box
                  sx={{
                    borderBottom: 1,
                    borderColor: "divider",
                    "& .MuiTab-root": {
                      textTransform: "none",
                      padding: "2px",
                    },
                  }}
                >

                  <TabList 
                    onChange={(event, newValue) => { 
                      handleTabValueChange(event, newValue);
                      if (businessGroupState.currentBusinessGroup) {
                        onTabSelected(newValue, businessGroupState.currentBusinessGroup);
                      }
                    }}
                    textColor="secondary" indicatorColor="secondary">
                    <Tab label="Beacons" value="beacons" />
                    {/* <Tab label="Archived" value="archived" />
                  <Tab label="Analytics" value="analytics" /> */}
                    {/* {(hasTabs() && auth.isBusinessUser()) && <Tab label="Categories" value="categories"/>} */}
                    {((hasTabs())) && <Tab label="Experience Details" value="experience" />}
                    {/* {((hasTabs() && auth.isBusinessUser()) || hasOwnerSubscriptionPermission() ) && <Tab label="Design" value="design" />} */}
                  </TabList>
                </Box>


                <TabPanel value="beacons" sx={{ padding: 0 }}>
                  <Box
                    sx={{
                      marginY: 2,
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <ToggleButtonGroup
                      exclusive
                      color="secondary"
                      value={viewMode}
                      onChange={(event, newValue) => {
                        if (newValue) {
                          handleViewModeChange(newValue);
                          if (businessGroupState.currentBusinessGroup) {
                            onViewSelected(newValue, businessGroupState.currentBusinessGroup);
                          }
                        }
                      }}
                      aria-label="Platform"
                      sx={{
                        "& .MuiToggleButton-root": {
                          border: "none",
                          textTransform: "none",
                          padding: "6px",
                        },
                      }}
                    >

                      <ToggleButton tabIndex={0} value={BeaconViewMode.Map}>
                        <MapOutlinedIcon sx={{ mr: 1 }} />
                        Map
                      </ToggleButton>
                      <ToggleButton tabIndex={1} value={BeaconViewMode.List}>
                        <FormatListBulletedOutlinedIcon sx={{ mr: 1 }} />
                        List
                      </ToggleButton>
                      <ToggleButton tabIndex={2} value={BeaconViewMode.Analytics}>
                        <AnalyticsOutlined sx={{ mr: 1 }} />
                        Analytics
                      </ToggleButton>
                      {/* <ToggleButton tabIndex={2} value={BeaconViewMode.Calendar}>
                    <CalendarMonthOutlinedIcon sx={{ mr: 1 }} />
                    Calendar
                  </ToggleButton>
                  <ToggleButton tabIndex={3} value={BeaconViewMode.Card}>
                    <GridViewOutlinedIcon sx={{ mr: 1 }} />
                    Cards
                  </ToggleButton> */}
                    </ToggleButtonGroup>
                    {getBeaconViewAction()}
                  </Box>
                  <Box>{getBeaconView()}
                  </Box>
                </TabPanel>
                <TabPanel value="experience" sx={{ padding: 1 }}>
                  <Box>{getExperienceViewMode()}</Box>
                </TabPanel>
                <TabPanel value="analytics" sx={{ padding: 1 }}>
                  <Box>{getBeaconView()}</Box>
                </TabPanel>
                <TabPanel value="design" sx={{ padding: 1 }}>
                  <Box
                    sx={{
                      margin: 2,
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <ToggleButtonGroup
                      exclusive
                      color="secondary"
                      value={viewMode}
                      onChange={(event, newValue) => {
                        if (newValue) {
                          handleViewModeChange(newValue);
                          if (businessGroupState.currentBusinessGroup) {
                            onViewSelected(newValue, businessGroupState.currentBusinessGroup);
                          }
                        }
                      }}
                      aria-label="Platform"
                      sx={{
                        "& .MuiToggleButton-root": {
                          border: "none",
                          textTransform: "none",
                          padding: "6px",
                        },
                      }}
                    >
                      <ToggleButton tabIndex={6} value={DesignViewMode.Category}>
                        <CategoryOutlinedIcon sx={{ mr: 1 }} />
                        Categories
                      </ToggleButton>
                      <ToggleButton tabIndex={7} value={DesignViewMode.QrCode}>
                        <QrCodeIcon sx={{ mr: 1 }} />
                        QR Code
                      </ToggleButton>
                      {!businessGroupState.currentBusinessGroup?.archived && <ToggleButton tabIndex={8} value={DesignViewMode.LandingPage}>
                        <ArticleOutlinedIcon sx={{ mr: 1 }} />
                        Landing Page
                      </ToggleButton>}
                    </ToggleButtonGroup>
                    {getBeaconViewAction()}
                  </Box>
                  <Box>{getDesignView()}
                  </Box>
                </TabPanel>
                <TabPanel value="create" sx={{ padding: 1 }}>
                      Create a New Experience
                </TabPanel>
                  </>
                )}
              </Box>
            </div>
          ) : (
            <div className={styles.beacons_mainContents}>
              <Box sx={{ width: "100%", typography: "body1", margin: 2 }}>
                {getPageView()}
              </Box>
            </div>
          )}
        </TabContext>
      </div>
      <TextInputDialog open={openCreateShareGroup} onClose={handleCloseNewShareGroup} title="Add a new Shared Group" inputLabel="Share Code" enterText="Add" />
      <TextInputDialog
        open={openEditGroup}
        onClose={closeEditGroupDialog}
        title="Edit Experience"
        inputLabel="Experience Name"
        enterText="Save"
        defaultValue={businessGroupState.currentBusinessGroup?.name}
      />
      <EditIndoorDialog
        open={openEditIndoor}
        onClose={closeEditIndoor}
        title="Edit Indoor Experience"
        enterText="Save"
        defaultName={businessGroupState.currentIndoorExperience?.name}
        defaultIdentifier={businessGroupState.currentIndoorExperience?.subdomain}
      />
      {/* <BeaconDialog
        onSubmitted={() => handleSubmitted()}
        mode={beaconDialogEditMode}
        isOpen={openBeaconDialog}
        beacon={selectedBeacon}
        onClose={() => setOpenBeaconDialog(false)}
      /> */}
      <BeaconImportDialog
        onSubmitted={() => handleSubmitted()}
        isOpen={openBeaconImportDialog}
        onClose={() => setOpenBeaconImportDialog(false)}
      />
      <AppConfirmDialog
        open={openEditSpecialBeaconMessage}
        onConfirm={() => {
          setOpenEditSpecialBeaconMessage(false);
        }}
        confirmButtonTitle="OK"
        disableDenyButton={true}
        title="Beacons with an API integration can not be edited directly."
        dialogTitle="Edit Beacon"
        content=""
      />
      <AppConfirmDialog
        open={openDeleteBeacon}
        onConfirm={() => {handleDeleteBeacon(); handleSubmitted();}}
        onDeny={() => setOpenDeleteBeacon(false)}
        confirmButtonTitle="Yes, Delete"
        denyButtonTitle="Cancel"
        title="Are you sure you want to delete this beacon? Once deleted it will be lost forever."
        dialogTitle="Delete Beacon"
        content=""
        buttonColour={AppColor.redError}
      />
      <AppConfirmDialog
        open={openExperienceError}
        onConfirm={() => {
          setOpenExperienceError(false);
        }}
        confirmButtonTitle="OK"
        disableDenyButton={true}
        title={`You are currently limited to ${experienceLimit} Experience(s). You need to upgrade your subscription to create more Experiences.`}
        dialogTitle="Upgrade Subscription"
        content=""
      />
      <AppConfirmDialog
        open={openBeaconsInExpError}
        onConfirm={() => {
          setOpenBeaconsInExpError(false);
        }}
        confirmButtonTitle="OK"
        disableDenyButton={true}
        title={`You are currently limited to ${beaconsInExpLimit} Beacons per Experience. You need to upgrade your subscription to create more Beacons inside this Experience.`}
        dialogTitle="Upgrade Subscription"
        content=""
      />
    </>
  );
};

export default BeaconsIndex;
